import {
  createCriterionForField,
  isCriterionAcceptChoice,
  isCriterionDeclineChoice,
  isCriterionTolerateChoice,
  toggleAcceptedChoiceInCriterion,
  toggleDeclinedChoiceInCriterion,
  toggleTolerateChoiceInCriterion,
} from 'lib/criteria';
import { useCallback } from 'react';
import { Criterion, FieldChoice, Quota, SelectChoiceField } from 'queries';

interface Props {
  field: SelectChoiceField;
  criterion?: Criterion;
  quotas?: Quota[];
  onCriterionChange?: (criterion: Criterion) => any;
  enabledTolerate?: boolean;
}

export const useCriterionChoiceProps = ({
  onCriterionChange,
  criterion,
  quotas,
  field,
  enabledTolerate,
}: Props) => {
  const acceptAllChoices = useCallback(() => {
    if (!onCriterionChange) {
      return;
    }
    let crit = criterion;
    if (!crit) {
      crit = createCriterionForField(field, true);
    }
    onCriterionChange(crit);
  }, [criterion, onCriterionChange, field]);

  const onAcceptClick = useCallback(
    (choice: FieldChoice) => {
      if (!onCriterionChange) {
        return;
      }
      let crit = criterion;
      if (!crit) {
        crit = createCriterionForField(field);
      }

      if (quotas?.length) {
        if (!quotas.find((c) => c.item === choice.ref)) {
          crit.quotas = [
            ...quotas,
            {
              item: choice.ref,
              value: 0,
              ref: crit.fieldRef,
            },
          ];
        } else {
          crit.quotas = quotas;
        }
      }

      crit = toggleAcceptedChoiceInCriterion(crit, choice);
      onCriterionChange(crit);
    },
    [onCriterionChange, criterion, quotas, field],
  );

  const onTolerateClick = useCallback(
    (choice: FieldChoice) => {
      if (!onCriterionChange) {
        return;
      }
      let crit = criterion;
      if (!crit) {
        crit = createCriterionForField(field);
      }

      if (quotas?.length) {
        if (!quotas.find((c) => c.item === choice.ref)) {
          crit.quotas = [
            ...quotas,
            {
              item: choice.ref,
              value: 0,
              ref: crit.fieldRef,
            },
          ];
        } else {
          crit.quotas = quotas;
        }
      }

      crit = toggleTolerateChoiceInCriterion(crit, choice);
      onCriterionChange(crit);
    },
    [criterion, onCriterionChange, quotas, field],
  );

  const onDeclineClick = useCallback(
    (choice: FieldChoice) => {
      if (!onCriterionChange) {
        return;
      }
      let crit = criterion;
      if (!crit) {
        crit = createCriterionForField(field);
      }

      const isCurrentlyDeclined = isCriterionDeclineChoice(
        criterion,
        choice,
        !enabledTolerate,
      );
      if (quotas?.length) {
        if (isCurrentlyDeclined) {
          if (!quotas.find((c) => c.item === choice.ref)) {
            crit.quotas = [
              ...quotas,
              {
                item: choice.ref,
                value: 0,
                ref: crit.fieldRef,
              },
            ];
          } else {
            crit.quotas = quotas;
          }
        } else {
          crit.quotas = quotas.filter((c) => c.item !== choice.ref);
        }
      }

      crit = toggleDeclinedChoiceInCriterion(crit, choice, !enabledTolerate);
      onCriterionChange(crit);
    },
    [onCriterionChange, criterion, quotas, enabledTolerate, field],
  );

  return {
    isChoiceDeclined: (choice: FieldChoice) =>
      isCriterionDeclineChoice(criterion, choice, !enabledTolerate),
    isChoiceAccepted: (choice: FieldChoice) =>
      isCriterionAcceptChoice(criterion, choice),
    isChoiceTolerated: (choice: FieldChoice) =>
      isCriterionTolerateChoice(criterion, choice),
    acceptAllChoices,
    onTolerateClick,
    onAcceptClick,
    onDeclineClick,
  };
};
